import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent } from '../ui/ResponsiveLayout';
import BrandSymbol from '../ui/BrandSymbol';
import Modal from '../ui/Modal';
import { trackDeathCertUploaded, trackSettlementStarted } from '../../utils/analytics';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.endofanera.ai';

interface DeathCertStatus {
  hasDeathCert: boolean;
  status: 'pending' | 'approved' | 'rejected' | null;
  submittedAt?: string;
  submittedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

interface EstatePreview {
  accessLevel: 'limited' | 'full';
  estateOwner: {
    name: string;
    email: string;
  };
  preview?: {
    message: string;
    accessList: string[];
  };
  estateData?: {
    about: unknown;
    inventory: unknown;
    distribution: unknown;
    legalEssentials: unknown;
    memories: unknown;
    professionalContacts: unknown;
  };
  deathCertStatus: string | null;
}

const ExecutorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { estateId } = useParams<{ estateId: string }>();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [estatePreview, setEstatePreview] = useState<EstatePreview | null>(null);
  const [deathCertStatus, setDeathCertStatus] = useState<DeathCertStatus | null>(null);
  
  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fetchEstateData = useCallback(async () => {
    if (!estateId) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('account-token');
      
      // Fetch estate preview
      const estateResponse = await fetch(`${API_BASE_URL}/api/settlement/${estateId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!estateResponse.ok) {
        const errorData = await estateResponse.json();
        throw new Error(errorData.error || 'Failed to fetch estate data');
      }
      
      const estateData = await estateResponse.json();
      setEstatePreview(estateData);
      
      // Fetch death certificate status
      const certResponse = await fetch(`${API_BASE_URL}/api/death-certificate/status/${estateId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (certResponse.ok) {
        const certData = await certResponse.json();
        setDeathCertStatus(certData);
      }
    } catch (err: unknown) {
      console.error('Error fetching estate data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [estateId]);

  useEffect(() => {
    fetchEstateData();
  }, [fetchEstateData]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setUploadError('File size must be less than 10MB');
        return;
      }
      setUploadFile(file);
      setUploadError(null);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !estateId) return;
    
    setUploading(true);
    setUploadError(null);
    
    try {
      const token = localStorage.getItem('account-token');
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target?.result?.toString().split(',')[1];
        
        const response = await fetch(`${API_BASE_URL}/api/death-certificate/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            estateOwnerId: estateId,
            fileData: base64Data,
            fileName: uploadFile.name,
            fileType: uploadFile.type,
            fileSize: uploadFile.size
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to upload death certificate');
        }
        
        trackDeathCertUploaded();
        setShowUploadModal(false);
        setUploadFile(null);
        fetchEstateData(); // Refresh data
      };
      
      reader.readAsDataURL(uploadFile);
    } catch (err: unknown) {
      console.error('Upload error:', err);
      setUploadError(err.message);
      setUploading(false);
    }
  };

  const getStatusBadge = () => {
    if (!deathCertStatus?.hasDeathCert) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-stone-100 text-stone-700">
          <span className="w-2 h-2 bg-stone-400 rounded-full mr-2"></span>
          No Death Certificate
        </span>
      );
    }
    
    switch (deathCertStatus.status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2 animate-pulse"></span>
            Awaiting Approval
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Full Access Granted
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
            Additional Info Needed
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <ResponsiveContainer background="stone">
        <ResponsiveContent>
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
            <p className="mt-4 text-stone-600">Loading estate information...</p>
          </div>
        </ResponsiveContent>
      </ResponsiveContainer>
    );
  }

  if (error) {
    return (
      <ResponsiveContainer background="stone">
        <ResponsiveContent>
          <div className="max-w-2xl mx-auto text-center py-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-stone-800 mb-2">Unable to Access Estate</h2>
            <p className="text-stone-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/settings')}
              className="px-6 py-2 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 transition-colors"
            >
              Return to Settings
            </button>
          </div>
        </ResponsiveContent>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-full max-w-4xl mb-4">
            <button
              onClick={() => navigate('/settings')}
              className="text-cyan-700 hover:text-cyan-800 text-sm font-medium flex items-center gap-2"
            >
              ← Back to Settings
            </button>
          </div>
          <BrandSymbol size="3xl" variant="dark" showText={true} className="mb-4" />
          <h1 className="font-display text-2xl font-semibold text-stone-800">
            {estatePreview?.estateOwner.name}'s Estate
          </h1>
          <p className="text-stone-600">You are an executor for this estate</p>
          <div className="mt-3">
            {getStatusBadge()}
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Death Certificate Section */}
          <section className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
            <h2 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Death Certificate
            </h2>

            {!deathCertStatus?.hasDeathCert ? (
              <div className="bg-stone-50 rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-stone-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <h3 className="font-medium text-stone-800 mb-2">Upload Death Certificate</h3>
                <p className="text-sm text-stone-600 mb-4">
                  To access the full estate information and begin the settlement process, 
                  please upload a certified copy of the death certificate.
                </p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-6 py-2 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 transition-colors"
                >
                  Upload Death Certificate
                </button>
              </div>
            ) : deathCertStatus.status === 'pending' ? (
              <div className="bg-yellow-50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-yellow-800 mb-1">Awaiting Verification</h3>
                    <p className="text-sm text-yellow-700 mb-2">
                      Your death certificate is being reviewed by our team. This typically takes 1-2 business days.
                    </p>
                    <p className="text-xs text-yellow-600">
                      Submitted by {deathCertStatus.submittedBy} on {new Date(deathCertStatus.submittedAt!).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ) : deathCertStatus.status === 'rejected' ? (
              <div className="bg-red-50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-red-800 mb-1">Additional Information Needed</h3>
                    <p className="text-sm text-red-700 mb-3">
                      {deathCertStatus.rejectionReason}
                    </p>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      Upload New Document
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-green-800 mb-1">Verified</h3>
                    <p className="text-sm text-green-700">
                      The death certificate has been verified. You now have full access to the estate.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Access Preview / Full Access Section */}
          {estatePreview?.accessLevel === 'limited' ? (
            <section className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
              <h2 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                What You'll Have Access To
              </h2>
              <p className="text-stone-600 mb-4">
                {estatePreview.preview?.message}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {estatePreview.preview?.accessList.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-stone-600">
                    <svg className="w-4 h-4 text-cyan-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </div>
                ))}
              </div>
            </section>
          ) : (
            <section className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Full Estate Access
                </h2>
                <button
                  onClick={() => { trackSettlementStarted(estateId); navigate(`/sframe-117/${estateId}`); }}
                  className="px-6 py-2 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 transition-colors flex items-center gap-2"
                >
                  Begin Settlement Process
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              <p className="text-stone-600 mb-6">
                You have been granted full access to {estatePreview?.estateOwner.name}'s estate. 
                Review the information below or begin the settlement process.
              </p>

              {/* Quick summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-stone-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-stone-500 mb-1">About</h3>
                  <p className="text-lg font-semibold text-stone-800">
                    {estatePreview?.estateData?.about?.answers ? 'Complete' : 'Incomplete'}
                  </p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-stone-500 mb-1">Inventory</h3>
                  <p className="text-lg font-semibold text-stone-800">
                    {Object.keys(estatePreview?.estateData?.inventory || {}).length} sections
                  </p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-stone-500 mb-1">Distribution</h3>
                  <p className="text-lg font-semibold text-stone-800">
                    {estatePreview?.estateData?.distribution?.beneficiaries?.length || 0} beneficiaries
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* Help Section */}
          <section className="bg-cyan-50 rounded-xl border border-cyan-200 p-6">
            <h2 className="text-lg font-semibold text-cyan-900 mb-2">Need Help?</h2>
            <p className="text-sm text-cyan-800 mb-4">
              Settling an estate can be complex. We're here to guide you through every step.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/frame-199')}
                className="px-4 py-2 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 transition-colors text-sm"
              >
                Visit Help Center
              </button>
              <a
                href="mailto:support@endofanera.ai"
                className="px-4 py-2 bg-white text-cyan-700 border border-cyan-300 rounded-lg hover:bg-cyan-50 transition-colors text-sm"
              >
                Contact Support
              </a>
            </div>
          </section>
        </div>

        {/* Upload Modal */}
        <Modal
          open={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadFile(null);
            setUploadError(null);
          }}
          title="Upload Death Certificate"
        >
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              Please upload a certified copy of the death certificate. Accepted formats: PDF, JPG, PNG (max 10MB).
            </p>
            
            <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="hidden"
                id="death-cert-upload"
              />
              <label
                htmlFor="death-cert-upload"
                className="cursor-pointer"
              >
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-stone-800 font-medium">{uploadFile.name}</span>
                  </div>
                ) : (
                  <div>
                    <svg className="w-10 h-10 text-stone-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-cyan-700 font-medium">Click to select file</span>
                    <span className="text-stone-500 block text-sm mt-1">or drag and drop</span>
                  </div>
                )}
              </label>
            </div>

            {uploadError && (
              <p className="text-sm text-red-600">{uploadError}</p>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadError(null);
                }}
                className="px-4 py-2 text-stone-600 hover:text-stone-800"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className={`px-6 py-2 rounded-lg text-white font-medium ${
                  !uploadFile || uploading
                    ? 'bg-stone-400 cursor-not-allowed'
                    : 'bg-cyan-700 hover:bg-cyan-800'
                }`}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </Modal>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default ExecutorDashboard;

